import React, { useState, useMemo, useEffect } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  TextField,
  Checkbox,
} from "@mui/material";
import GlobalIcon from "../GlobalIcon/GlobalIcon";
import {
  clampQtyLots,
  deriveAbsDisclosedQty,
  clampPriceToCircuitLimits,
  isLimitOrderUiType,
} from "../../pages/OrderInitiatePage/utils";
import styles from "./ScriptComponent.module.css";

/** Set on document.body on Tab; cleared on pointerdown — focus rings in CSS only apply with this */
let scriptTabFocusBodyListenerCount = 0;

const SelectDropdownIcon = (props) => <GlobalIcon iconName="SelectDropdownArrow" {...props} />;

const SCRIPT_SELECT_MENU_PROPS = {
  disablePortal: false,
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  MenuListProps: { dense: true },
  slotProps: {
    paper: {
      sx: {
        maxHeight: 280,
        "& .MuiMenuItem-root": {
          fontSize: "12px",
          fontWeight: 400,
          minHeight: "26px",
          py: "4px",
          px: "10px",
          lineHeight: 1.35,
        },
      },
    },
  },
};

function cycleOptionList(options, currentValue, key) {
  if (!options?.length || options.length < 2) return undefined;
  const idx = options.indexOf(currentValue);
  const i = idx >= 0 ? idx : 0;
  if (key === "ArrowDown") return options[(i + 1) % options.length];
  if (key === "ArrowUp") return options[(i - 1 + options.length) % options.length];
  return undefined;
}

function mergeSelectMenuPropsWithArrow(onMenuListKeyDown) {
  return {
    ...SCRIPT_SELECT_MENU_PROPS,
    MenuListProps: {
      ...SCRIPT_SELECT_MENU_PROPS.MenuListProps,
      onKeyDown: (e) => onMenuListKeyDown(e),
    },
  };
}

const PRODUCT_OPTIONS = ["DELIVERY", "INTRADAY", "MARGIN"];

/** Format API expiry "2026-02-24" to display "24-02-2026" */
function formatExpiryDisplay(apiDate) {
  if (!apiDate || typeof apiDate !== "string") return apiDate;
  const [y, m, d] = apiDate.split("-");
  return d && m && y ? `${d}-${m}-${y}` : apiDate;
}

function getRawScripField(selection, field) {
  if (!selection) return undefined;
  switch (field) {
    case "exchange":
      return selection.exchange;
    case "scripType":
      return selection.segment;
    case "tradingSymbol":
      return selection.symbol;
    case "category":
      return selection.option_type;
    case "strike":
      return selection.strike_price;
    case "expiry":
      return selection.expiry_date;
    default:
      return undefined;
  }
}

const SCRIP_DROPDOWN_FIELDS = [
  { label: "Exchange", field: "exchange" },
  { label: "Scrip Type", field: "scripType" },
  { label: "Trading Symbol", field: "tradingSymbol" },
  { label: "Category", field: "category" },
  { label: "Strike", field: "strike" },
  { label: "Expiry", field: "expiry" },
];

const EMPTY_SCRIP_VALUES = {
  exchange: "",
  scripType: "",
  tradingSymbol: "",
  category: "",
  strike: "",
  expiry: "",
};

const ORDER_TYPE_UI_OPTIONS = ["Bid/Ask", "Expiry", "Limit Order"];

function ScriptComponent({
  id,
  scriptIndex = 0,
  dropdownOptions,
  selection,
  onSelectionChange,
  onDelete,
  onAddScript,
  animateIn,
  selected,
  onSelectedChange,
  productOptions = [],
  orderTypeOptions = [],
  validityOptions = [],
  quoteMetrics = [],
  orderBookRows = [],
  orderBookBuyRows,
  orderBookSellRows,
  bidFooter = [],
  askFooter = [],
  headerSymbol,
  marginRequired,
  defaultPrice,
  defaultQtyLots,
  defaultAbsDisQty,
  quoteLotSize,
  lowerCircuitLimit,
  upperCircuitLimit,
}) {
  const displayHeaderSymbol = headerSymbol ?? "—";
  const displayMargin = marginRequired != null && marginRequired !== "" ? `MARGIN REQUIRED- ${marginRequired}` : "—";
  const buyRows = orderBookBuyRows ?? orderBookRows;
  const sellRows = orderBookSellRows ?? orderBookRows;
  const isControlled = selection != null && typeof onSelectionChange === "function";
  const opts = useMemo(() => {
    if (!dropdownOptions) return null;
    const ex = dropdownOptions.unique_exchange_options || [];
    const seg = dropdownOptions.unique_segment_options || [];
    const sym = dropdownOptions.unique_symbol_options || [];
    const cat = dropdownOptions.unique_option_type_options || [];
    const strikes = dropdownOptions.unique_strike_price_options ?? [];
    const exp = dropdownOptions.unique_expiry_date_options || [];
    return {
      exchange: ex,
      scripType: seg,
      tradingSymbol: sym,
      category: cat,
      strike: strikes.map((s) => String(s)),
      expiry: exp.map((e) => formatExpiryDisplay(e)),
    };
  }, [dropdownOptions]);

  const [localScrip, setLocalScrip] = useState(EMPTY_SCRIP_VALUES);

  const exchangeValue = useMemo(() => {
    const raw = isControlled ? selection?.exchange : localScrip.exchange;
    return String(raw ?? "").trim();
  }, [isControlled, selection?.exchange, localScrip.exchange]);

  /** Strike & expiry apply only to NFO; other exchanges keep them disabled. */
  const strikeExpiryDisabled = exchangeValue.toUpperCase() !== "NFO";

  /** NFO: disclosed qty is driven by lots / API rules — not user-editable. */
  const absDisQtyDisabledNfo = exchangeValue.toUpperCase() === "NFO";

  const getDisplayValue = (field) => {
    if (isControlled) {
      const raw = getRawScripField(selection, field);
      // Respect explicit "" so cleared fields stay empty (do not substitute first API option)
      if (raw !== undefined && raw !== null) {
        if (field === "expiry") return raw === "" ? "" : formatExpiryDisplay(raw);
        if (field === "strike") return raw === "" ? "" : String(raw);
        return String(raw);
      }
      const fromOpts = opts?.[field]?.[0];
      return fromOpts ?? localScrip[field] ?? "";
    }
    return localScrip[field];
  };

  const getOptionsForField = (field) => opts?.[field] ?? [];

  const getScripSelectValue = (field) => {
    const v = getDisplayValue(field);
    const options = getOptionsForField(field);
    if (!options.length) return v ?? "";
    if (v === "" || v == null || !options.includes(v)) return options[0];
    return v;
  };

  const handleScripChange = (field, value) => {
    if (isControlled) {
      onSelectionChange(scriptIndex, field, value);
      return;
    }
    setLocalScrip((prev) => ({ ...prev, [field]: value }));
  };

  const handleScripArrowKey = (field, e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const options = getOptionsForField(field);
    const current = getScripSelectValue(field);
    const next = cycleOptionList(options, current, e.key);
    if (next !== undefined && next !== current) {
      e.preventDefault();
      e.stopPropagation();
      handleScripChange(field, next);
    }
  };

  useEffect(() => {
    if (!isControlled || !onSelectionChange || !opts || !selection) return;
    for (const { field } of SCRIP_DROPDOWN_FIELDS) {
      if ((field === "strike" || field === "expiry") && strikeExpiryDisabled) {
        continue;
      }
      const options = opts[field];
      if (!options?.length) continue;
      const raw = getRawScripField(selection, field);
      let v;
      if (raw !== undefined && raw !== null) {
        if (field === "expiry") v = raw === "" ? "" : formatExpiryDisplay(raw);
        else if (field === "strike") v = raw === "" ? "" : String(raw);
        else v = String(raw);
      } else {
        const fromOpts = opts[field]?.[0];
        v = fromOpts ?? "";
      }
      if (field !== "exchange" && v === "") {
        continue;
      }
      if (!options.includes(v)) {
        onSelectionChange(scriptIndex, field, options[0]);
        return;
      }
    }
  }, [isControlled, opts, scriptIndex, onSelectionChange, selection, strikeExpiryDisabled]);

  useEffect(() => {
    if (!opts || isControlled) return;
    setLocalScrip((prev) => {
      const next = { ...prev };
      SCRIP_DROPDOWN_FIELDS.forEach(({ field }) => {
        if ((field === "strike" || field === "expiry") && strikeExpiryDisabled) {
          return;
        }
        const options = opts[field];
        if (options?.length && !options.includes(prev[field])) next[field] = options[0];
      });
      return next;
    });
  }, [opts, isControlled, strikeExpiryDisabled]);

  const [localState, setLocalState] = useState({
    transactionType: "Buy",
    qtyLots: "",
    absDisQty: "",
    productType: "",
    orderType: "",
    price: "",
    validity: "",
  });

  const getProp = (key) => isControlled && selection?.[key] !== undefined ? selection[key] : localState[key];
  const setProp = (key, val) => {
    if (isControlled) onSelectionChange(scriptIndex, key, val);
    else setLocalState(prev => ({ ...prev, [key]: val }));
  };

  const transactionType = getProp("transactionType");
  const setTransactionType = (val) => setProp("transactionType", val);
  const productType = getProp("productType");
  const setProductType = (val) => setProp("productType", val);
  const orderType = getProp("orderType");
  const setOrderType = (val) => setProp("orderType", val);
  const validity = getProp("validity");
  const setValidity = (val) => setProp("validity", val);
  const parentQtyLots = getProp("qtyLots");
  const parentAbsDisQty = getProp("absDisQty");
  const parentPrice = getProp("price");

  const [localText, setLocalText] = useState({
    qtyLots: parentQtyLots,
    absDisQty: parentAbsDisQty,
    price: parentPrice,
  });

  const qtyLots = localText.qtyLots;
  const absDisQty = localText.absDisQty;
  const price = localText.price;

  const orderTypeRef = React.useRef(orderType);
  orderTypeRef.current = orderType;
  const lowerCircuitRef = React.useRef(lowerCircuitLimit);
  lowerCircuitRef.current = lowerCircuitLimit;
  const upperCircuitRef = React.useRef(upperCircuitLimit);
  upperCircuitRef.current = upperCircuitLimit;
  const priceStrRef = React.useRef("");
  priceStrRef.current = String(price ?? "");

  const userClearedQtyRef = React.useRef(false);
  const userClearedAbsDisQtyRef = React.useRef(false);
  const userClearedPriceRef = React.useRef(false);
  const priceInputFocusedRef = React.useRef(false);

  useEffect(() => { setLocalText(p => ({ ...p, qtyLots: parentQtyLots })); }, [parentQtyLots]);
  useEffect(() => { setLocalText(p => ({ ...p, absDisQty: parentAbsDisQty })); }, [parentAbsDisQty]);
  useEffect(() => { setLocalText(p => ({ ...p, price: parentPrice })); }, [parentPrice]);

  const typingTimers = React.useRef({});
  const qtyLotsFallback = defaultQtyLots != null && String(defaultQtyLots).trim() !== "" ? defaultQtyLots : 1;

  const handleTextChange = (key, val) => {
    setLocalText(prev => ({ ...prev, [key]: val }));
    if (key === "qtyLots" && val === "") userClearedQtyRef.current = true;
    if (key === "absDisQty" && val === "") userClearedAbsDisQtyRef.current = true;
    if (key === "price") userClearedPriceRef.current = val === "";
    if (typingTimers.current[key]) clearTimeout(typingTimers.current[key]);
    typingTimers.current[key] = setTimeout(() => {
      if (key === "qtyLots") {
        if (val === "") {
          setLocalText((prev) => ({ ...prev, qtyLots: "", absDisQty: "" }));
          setProp("qtyLots", "");
          setProp("absDisQty", "");
          return;
        }
        userClearedQtyRef.current = false;
        const c = String(clampQtyLots(val, qtyLotsFallback));
        const derived = deriveAbsDisclosedQty(c, exchangeValue, quoteLotSize);
        setLocalText((prev) => ({
          ...prev,
          qtyLots: c,
          ...(derived !== "" ? { absDisQty: derived } : {}),
        }));
        setProp("qtyLots", c);
        if (derived !== "") {
          userClearedAbsDisQtyRef.current = false;
          setProp("absDisQty", derived);
        }
        return;
      }
      if (key === "price") {
        const clamped = clampPriceToCircuitLimits(
          val,
          orderTypeRef.current,
          lowerCircuitRef.current,
          upperCircuitRef.current
        );
        if (clamped !== val) {
          setLocalText((prev) => ({ ...prev, price: clamped }));
        }
        setProp("price", clamped);
        return;
      }
      setProp(key, val);
    }, 400);
  };

  const handleQtyLotsBlur = () => {
    if (typingTimers.current.qtyLots) {
      clearTimeout(typingTimers.current.qtyLots);
      typingTimers.current.qtyLots = null;
    }
    if (qtyLots === "" && userClearedQtyRef.current) {
      setProp("qtyLots", "");
      setProp("absDisQty", "");
      setLocalText((prev) => ({ ...prev, absDisQty: "" }));
      return;
    }
    const c = String(clampQtyLots(qtyLots, qtyLotsFallback));
    const derived = deriveAbsDisclosedQty(c, exchangeValue, quoteLotSize);
    setLocalText((prev) => ({
      ...prev,
      qtyLots: c,
      ...(derived !== "" ? { absDisQty: derived } : {}),
    }));
    setProp("qtyLots", c);
    if (derived !== "") {
      userClearedAbsDisQtyRef.current = false;
      setProp("absDisQty", derived);
    }
  };

  const handlePriceBlur = () => {
    priceInputFocusedRef.current = false;
    if (typingTimers.current.price) {
      clearTimeout(typingTimers.current.price);
      typingTimers.current.price = null;
    }
    const raw = price ?? "";
    const trimmed = String(raw).trim();
    let next = String(raw);
    if (trimmed === "") {
      next = "";
    } else if (isLimitOrderUiType(orderType)) {
      next = clampPriceToCircuitLimits(raw, orderType, lowerCircuitLimit, upperCircuitLimit);
    }
    if (next !== String(raw)) {
      setLocalText((prev) => ({ ...prev, price: next }));
    }
    setProp("price", next);
  };

  const setQtyLots = (val) => handleTextChange("qtyLots", val);
  const setAbsDisQty = (val) => handleTextChange("absDisQty", val);
  const setPrice = (val) => handleTextChange("price", val);

  const [localHeaderSelected, setLocalHeaderSelected] = useState(false);
  const isHeaderCheckboxControlled = typeof selected === "boolean" && typeof onSelectedChange === "function";
  const headerSelected = isHeaderCheckboxControlled ? selected : localHeaderSelected;
  const setHeaderSelected = isHeaderCheckboxControlled
    ? (checked) => onSelectedChange(checked)
    : setLocalHeaderSelected;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (productType === "") setProductType("DELIVERY"); }, [productType]);
  useEffect(() => {
    const current = orderType || "";
    if (!ORDER_TYPE_UI_OPTIONS.includes(current)) {
      setOrderType(ORDER_TYPE_UI_OPTIONS[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType]);
  useEffect(() => {
    if (validityOptions?.length > 0) {
      const currentValidity = validity || "";
      if (!currentValidity || !validityOptions.includes(currentValidity)) {
        setValidity(validityOptions[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validityOptions, validity]);
  /* Poll sync: get_quote_data updates last_price → defaultPrice; keep Price field in sync unless user cleared or is editing. */
  useEffect(() => {
    if (defaultPrice == null || defaultPrice === "") return;
    if (userClearedPriceRef.current) return;
    if (priceInputFocusedRef.current) return;
    if (typingTimers.current.price) return;
    let nextPrice = defaultPrice;
    if (isLimitOrderUiType(orderType)) {
      nextPrice = clampPriceToCircuitLimits(
        defaultPrice,
        orderType,
        lowerCircuitLimit,
        upperCircuitLimit
      );
    }
    setLocalText((prev) => ({ ...prev, price: nextPrice }));
    if (isControlled) {
      onSelectionChange(scriptIndex, "price", nextPrice);
    } else {
      setLocalState((prev) => ({ ...prev, price: nextPrice }));
    }
  }, [
    defaultPrice,
    orderType,
    lowerCircuitLimit,
    upperCircuitLimit,
    isControlled,
    scriptIndex,
    onSelectionChange,
  ]);

  useEffect(() => {
    if (!isLimitOrderUiType(orderType)) return;
    const raw = priceStrRef.current;
    const s = String(raw ?? "").trim();
    if (s === "") return;
    const clamped = clampPriceToCircuitLimits(
      raw,
      orderType,
      lowerCircuitLimit,
      upperCircuitLimit
    );
    if (clamped === String(raw)) return;
    setLocalText((prev) => ({ ...prev, price: clamped }));
    if (isControlled) {
      onSelectionChange(scriptIndex, "price", clamped);
    } else {
      setLocalState((prev) => ({ ...prev, price: clamped }));
    }
  }, [orderType, lowerCircuitLimit, upperCircuitLimit, isControlled, scriptIndex, onSelectionChange]);
  useEffect(() => {
    userClearedPriceRef.current = false;
  }, [headerSymbol]);
  useEffect(() => { userClearedQtyRef.current = false; }, [defaultQtyLots]);
  useEffect(() => { userClearedAbsDisQtyRef.current = false; }, [defaultAbsDisQty]);
  useEffect(() => {
    if (defaultQtyLots == null || defaultQtyLots === "" || qtyLots !== "" || userClearedQtyRef.current) return;
    const c = String(clampQtyLots(defaultQtyLots, 1));
    const derived = deriveAbsDisclosedQty(c, exchangeValue, quoteLotSize);
    setLocalText((prev) => ({
      ...prev,
      qtyLots: c,
      ...(derived !== "" ? { absDisQty: derived } : {}),
    }));
    if (isControlled) {
      onSelectionChange(scriptIndex, "qtyLots", c);
      if (derived !== "") onSelectionChange(scriptIndex, "absDisQty", derived);
    } else {
      setLocalState((prev) => ({
        ...prev,
        qtyLots: c,
        ...(derived !== "" ? { absDisQty: derived } : {}),
      }));
    }
  }, [defaultQtyLots, qtyLots, isControlled, scriptIndex, onSelectionChange, exchangeValue, quoteLotSize]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (defaultAbsDisQty != null && defaultAbsDisQty !== "" && absDisQty === "" && !userClearedAbsDisQtyRef.current) setAbsDisQty(defaultAbsDisQty); }, [defaultAbsDisQty, absDisQty]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const t = String(qtyLots || "").trim();
    if (t === "") return;
    const derived = deriveAbsDisclosedQty(t, exchangeValue, quoteLotSize);
    if (derived === "") return;
    userClearedAbsDisQtyRef.current = false;
    setProp("absDisQty", derived);
    setLocalText((prev) => (prev.absDisQty === derived ? prev : { ...prev, absDisQty: derived }));
  }, [quoteLotSize, exchangeValue]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const productOptionsForExchange = useMemo(() => {
    if (exchangeValue.toUpperCase() === "NFO") {
      return PRODUCT_OPTIONS.filter((o) => o !== "MARGIN");
    }
    return PRODUCT_OPTIONS;
  }, [exchangeValue]);

  const productSelectDisplayValue = useMemo(() => {
    const opts = productOptionsForExchange;
    if (!opts.length) return productType || "";
    if (productType && opts.includes(productType)) return productType;
    return opts[0] ?? "";
  }, [productOptionsForExchange, productType]);

  useEffect(() => {
    const opts = productOptionsForExchange;
    if (!opts.length) return;
    if (!productType || !opts.includes(productType)) {
      const first = opts[0];
      if (first != null && productType !== first) setProductType(first);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType, productOptionsForExchange]);

  const handleProductArrowKey = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const next = cycleOptionList(productOptionsForExchange, productSelectDisplayValue, e.key);
    if (next !== undefined && next !== productSelectDisplayValue) {
      e.preventDefault();
      e.stopPropagation();
      setProductType(next);
    }
  };

  const orderTypeSelectValue = orderType || ORDER_TYPE_UI_OPTIONS[0];
  const handleOrderTypeArrowKey = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const next = cycleOptionList(ORDER_TYPE_UI_OPTIONS, orderTypeSelectValue, e.key);
    if (next !== undefined && next !== orderTypeSelectValue) {
      e.preventDefault();
      e.stopPropagation();
      setOrderType(next);
    }
  };

  const validitySelectValue = validity || validityOptions[0] || "";
  const handleValidityArrowKey = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    if (!validityOptions?.length || validityOptions.length < 2) return;
    const next = cycleOptionList(validityOptions, validitySelectValue, e.key);
    if (next !== undefined && next !== validitySelectValue) {
      e.preventDefault();
      e.stopPropagation();
      setValidity(next);
    }
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Tab") {
        document.body.dataset.scriptTabFocus = "true";
      }
    };
    const onPointerDown = () => {
      delete document.body.dataset.scriptTabFocus;
    };
    scriptTabFocusBodyListenerCount += 1;
    if (scriptTabFocusBodyListenerCount === 1) {
      document.addEventListener("keydown", onKeyDown, true);
      document.addEventListener("pointerdown", onPointerDown, true);
    }
    return () => {
      scriptTabFocusBodyListenerCount -= 1;
      delete document.body.dataset.scriptTabFocus;
      if (scriptTabFocusBodyListenerCount === 0) {
        document.removeEventListener("keydown", onKeyDown, true);
        document.removeEventListener("pointerdown", onPointerDown, true);
      }
    };
  }, []);

  return (
    <div className={`${styles.scriptWrapper} ${animateIn ? styles.scriptEnter : ""}`}>
        <div className={`${styles.scriptContainer} ${transactionType === "Buy" ? styles.scriptContainerBuy : styles.scriptContainerSell}`}>
          <div className={styles.headerBar}>
            <div className={styles.headerLeft}>
              <Checkbox
                checked={headerSelected}
                onChange={(e) => setHeaderSelected(e.target.checked)}
                size="small"
                className={styles.headerCheckbox}
              />
              <span className={styles.headerQuote}>QUOTE</span>
              <span className={styles.headerSeparator}>|</span>
              <span className={styles.headerSymbol}>{displayHeaderSymbol}</span>
              <span className={styles.headerSeparator}>|</span>
              <span className={styles.headerMargin}>{displayMargin}</span>
            </div>
            {onDelete && (
              <button type="button" className={styles.headerDelete} aria-label="Delete script" onClick={() => onDelete(id, scriptIndex)}>
                <GlobalIcon iconName="ScriptDeleteIcon" className={styles.headerDeleteIcon} />
              </button>
            )}
          </div>

          <div className={`${styles.scriptBody} ${transactionType === "Buy" ? styles.scriptBodyBuy : styles.scriptBodySell}`}>
            <div className={styles.paramsRow}>
              {SCRIP_DROPDOWN_FIELDS.map(({ label, field }) => {
                const isStrikeOrExpiry = field === "strike" || field === "expiry";
                const disabledStrikeExpiry = isStrikeOrExpiry && strikeExpiryDisabled;
                return (
                  <div key={field} className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>{label}</label>
                    <FormControl size="small" fullWidth>
                      <Select
                        IconComponent={SelectDropdownIcon}
                        disabled={disabledStrikeExpiry}
                        displayEmpty={disabledStrikeExpiry}
                        value={disabledStrikeExpiry ? "" : getScripSelectValue(field)}
                        renderValue={disabledStrikeExpiry ? () => "—" : undefined}
                        onChange={(e) => handleScripChange(field, e.target.value)}
                        onKeyDown={(e) => {
                          if (disabledStrikeExpiry) return;
                          handleScripArrowKey(field, e);
                        }}
                        className={styles.select}
                        MenuProps={mergeSelectMenuPropsWithArrow((e) => handleScripArrowKey(field, e))}
                      >
                        {disabledStrikeExpiry ? (
                          <MenuItem value="" disabled>
                            —
                          </MenuItem>
                        ) : (
                          getOptionsForField(field).map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </div>
                );
              })}
            </div>

            <div className={styles.quoteGrid}>
              <div className={styles.quoteGridInner}>
                {quoteMetrics.map((m) => (
                  <div key={m.label} className={styles.quoteBlock}>
                    <div className={`${styles.quoteValue} ${m.positive ? styles.quotePositive : ""}`}>{m.value}</div>
                    <div className={styles.quoteLabel}>{m.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.transactionRow}>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Transaction Type</label>
                <div className={styles.buySellGroup}>
                  <button
                    type="button"
                    className={`${styles.buySellBtn} ${transactionType === "Buy" ? styles.buySellActive : ""}`}
                    onClick={() => setTransactionType("Buy")}
                  >
                    Buy
                  </button>
                  <button
                    type="button"
                    className={`${styles.buySellBtn} ${transactionType === "Sell" ? styles.buySellActive : ""}`}
                    onClick={() => setTransactionType("Sell")}
                  >
                    Sell
                  </button>
                </div>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Qty/Lots</label>
                <TextField
                  size="small"
                  value={qtyLots}
                  onChange={(e) => setQtyLots(e.target.value)}
                  onBlur={handleQtyLotsBlur}
                  className={styles.input}
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Abs./Dis. Quantity</label>
                <TextField
                  size="small"
                  value={absDisQty}
                  onChange={(e) => setAbsDisQty(e.target.value)}
                  className={styles.input}
                  disabled={absDisQtyDisabledNfo}
                  title={absDisQtyDisabledNfo ? "Not editable for NFO — value follows quantity/lot rules" : undefined}
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Product Type</label>
                <FormControl size="small" fullWidth>
                  <Select
                    IconComponent={SelectDropdownIcon}
                    value={productSelectDisplayValue}
                    onChange={(e) => setProductType(e.target.value)}
                    onKeyDown={handleProductArrowKey}
                    className={styles.select}
                    MenuProps={mergeSelectMenuPropsWithArrow(handleProductArrowKey)}
                  >
                    {productOptionsForExchange.map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Order Type</label>
                <FormControl size="small" fullWidth>
                  <Select
                    IconComponent={SelectDropdownIcon}
                    value={orderType || ORDER_TYPE_UI_OPTIONS[0]}
                    onChange={(e) => setOrderType(e.target.value)}
                    onKeyDown={handleOrderTypeArrowKey}
                    className={styles.select}
                    MenuProps={mergeSelectMenuPropsWithArrow(handleOrderTypeArrowKey)}
                  >
                    {ORDER_TYPE_UI_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Price</label>
                <TextField
                  size="small"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  onFocus={() => {
                    priceInputFocusedRef.current = true;
                  }}
                  onBlur={handlePriceBlur}
                  className={styles.input}
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Validity</label>
                <FormControl size="small" fullWidth>
                  <Select
                    IconComponent={SelectDropdownIcon}
                    value={validity || validityOptions[0] || ""}
                    onChange={(e) => setValidity(e.target.value)}
                    onKeyDown={handleValidityArrowKey}
                    className={styles.select}
                    MenuProps={mergeSelectMenuPropsWithArrow(handleValidityArrowKey)}
                  >
                    {validityOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>

          <div className={styles.advancedRow}>
            <div className={styles.scheduleOrderPlusWrap}>
              <button type="button" className={styles.scheduleOrderPlusBtn} aria-label="Add script" onClick={() => onAddScript?.()}>
                <GlobalIcon iconName="AddIcon" className={styles.scheduleOrderPlusIcon} />
              </button>
            </div>
          </div>
        </div>

        <div className={styles.orderBookRight}>
          <div className={styles.orderBookTablesRow}>
            <div className={styles.orderBookTableBuy}>
              <div className={styles.orderBookHeader}>
                <span>ORDER</span>
                <span>QUANTITY</span>
                <span>PRICE</span>
              </div>
              <div className={styles.orderBookBody}>
                {buyRows.map((row, i) => (
                  <div key={`buy-${i}-${row.price}`} className={styles.orderBookRow}>
                    <span>{row.order}</span>
                    <span>{row.quantity}</span>
                    <span>{row.price}</span>
                  </div>
                ))}
              </div>
              <div className={styles.orderBookFooter}>
                {bidFooter.map((m) => (
                  <div key={m.label} className={styles.orderBookFooterItem}>
                    <span className={styles.orderBookFooterLabel}>{m.label}</span>
                    <span className={styles.orderBookFooterValue}>{m.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.orderBookTableSell}>
              <div className={styles.orderBookHeader}>
                <span>ORDER</span>
                <span>QUANTITY</span>
                <span>PRICE</span>
              </div>
              <div className={styles.orderBookBody}>
                {sellRows.map((row, i) => (
                  <div key={`sell-${i}-${row.price}`} className={styles.orderBookRow}>
                    <span>{row.order}</span>
                    <span>{row.quantity}</span>
                    <span>{row.price}</span>
                  </div>
                ))}
              </div>
              <div className={styles.orderBookFooter}>
                {askFooter.map((m) => (
                  <div key={m.label} className={styles.orderBookFooterItem}>
                    <span className={styles.orderBookFooterLabel}>{m.label}</span>
                    <span className={styles.orderBookFooterValue}>{m.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}

export default React.memo(ScriptComponent);
